/**
 * SPEC-TrainingSimulator §7.2 — bounding boxes in the key editor
 * (layout revision, 2026-09-09).
 *
 * Any question may carry boxes on POD photos (`photo:N`), stored on the
 * question's key entry as `boxes: [{ target, x, y, w, h }]`, normalised
 * 0..1 to the photo's natural size so they survive any render size. Text on
 * the cards is pointed at with anchored highlights instead (text-highlight.tsx)
 * because card text reflows and a box would drift. Every question's layer is
 * drawn; only the active question's layer is editable, the others show
 * dimmed for context.
 *
 *   BoxOverlay    — renders a photo's boxes and handles the drag-to-draw for
 *                   the active layer.
 *   PhotoBoxPanel — the Photos card (POD / Verified past) with the overlay
 *                   fitted to the object-fit:contain image.
 *
 * The live PhotosPanel is left untouched — this module exists so the key
 * editor can draw without adding editor-only props to an agent component.
 */

import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { Starling } from "@/lib/starling-brand";
import type { SimulatorSnapshotPhoto, SimulatorSnapshotVerifiedPast } from "@shared/simulator-helpers";

import type { PhotoBox } from "@/lib/simulator-key-pointers";

export type { PhotoBox };
/** A question's boxes: `kind` is the question id, `label` its short name for the chip. */
export interface PhotoBoxLayer { kind: string; label: string; boxes: PhotoBox[] }

// Every box is the same very bright green (Sarah, 2026-09-09) so it reads
// against any photo; the label chip says which question it belongs to.
// Text in the UI uses the darker ink because lime on white is unreadable.
export const BOX_STROKE = "#00FF41";
export const BOX_INK = "#15803D";

const T = {
  card: "#FFFFFF",
  sub: "#F3F4F6",
  border: "#E5E7EB",
  text: "#111827",
  txt2: "#6B7280",
  txt3: "#9CA3AF",
  purple: Starling.purple,
  purpleSub: "rgba(89,47,150,.12)",
  goodCalm: "#166534",
  goodCalmBg: "#DCFCE7",
} as const;

const MIN_BOX = 0.01;
const round = (v: number) => Math.round(v * 10000) / 10000;
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

interface Fit { x: number; y: number; w: number; h: number }
interface Draft { x0: number; y0: number; x1: number; y1: number }

/** Where an object-fit:contain image sits inside its container. */
function fitRect(cw: number, ch: number, nw: number, nh: number): Fit | null {
  if (!cw || !ch || !nw || !nh) return null;
  const s = Math.min(cw / nw, ch / nh);
  const w = nw * s;
  const h = nh * s;
  return { x: (cw - w) / 2, y: (ch - h) / 2, w, h };
}

function useElementSize<E extends HTMLElement>() {
  const ref = useRef<E | null>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, size };
}

// ─── Generic overlay ─────────────────────────────────────────────────────────

export interface BoxOverlayProps {
  /** Photo the boxes belong to (`photo:2`). */
  target: string;
  /** Where the drawable area sits inside the overlay's parent, in px. Null → nothing renders. */
  fit: Fit | null;
  layers: PhotoBoxLayer[];
  activeKind: string | null;
  onAdd: (box: PhotoBox) => void;
  onRemove: (index: number) => void;
  /** Runner: the active layer is emphasised (solid) but nothing is drawable or removable. */
  readOnly?: boolean;
}

export function BoxOverlay({ target, fit, layers, activeKind, onAdd, onRemove, readOnly }: BoxOverlayProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const canDraw = !!activeKind && !!fit && !readOnly;

  const toNorm = (ev: ReactPointerEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el || !fit) return null;
    const r = el.getBoundingClientRect();
    return { x: clamp01((ev.clientX - r.left - fit.x) / fit.w), y: clamp01((ev.clientY - r.top - fit.y) / fit.h) };
  };
  const onDown = (ev: ReactPointerEvent<HTMLDivElement>) => {
    if (!canDraw || ev.button !== 0) return;
    const p = toNorm(ev);
    if (!p) return;
    ev.preventDefault();
    ev.currentTarget.setPointerCapture(ev.pointerId);
    setDraft({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
  };
  const onMove = (ev: ReactPointerEvent<HTMLDivElement>) => {
    if (!draft) return;
    const p = toNorm(ev);
    if (p) setDraft({ ...draft, x1: p.x, y1: p.y });
  };
  const onUp = (ev: ReactPointerEvent<HTMLDivElement>) => {
    if (!draft) return;
    const p = toNorm(ev) ?? { x: draft.x1, y: draft.y1 };
    const x = Math.min(draft.x0, p.x);
    const y = Math.min(draft.y0, p.y);
    const w = Math.abs(p.x - draft.x0);
    const h = Math.abs(p.y - draft.y0);
    setDraft(null);
    if (w >= MIN_BOX && h >= MIN_BOX) onAdd({ target, x: round(x), y: round(y), w: round(w), h: round(h) });
  };
  const px = (b: { x: number; y: number; w: number; h: number }) =>
    fit ? { left: fit.x + b.x * fit.w, top: fit.y + b.y * fit.h, width: b.w * fit.w, height: b.h * fit.h } : null;

  return (
    <div
      ref={ref}
      data-testid={`box-overlay-${target}`}
      data-drawable={canDraw ? "true" : "false"}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={() => setDraft(null)}
      style={{
        position: "absolute", inset: 0, zIndex: 2,
        pointerEvents: canDraw ? "auto" : "none",
        cursor: canDraw ? "crosshair" : "default",
        touchAction: canDraw ? "none" : undefined,
        userSelect: canDraw ? "none" : undefined,
      }}
    >
      {fit && layers.map((layer) => {
        const active = layer.kind === activeKind;
        return layer.boxes.map((b, i) => {
          if (b.target !== target) return null;
          const r = px(b);
          if (!r) return null;
          return (
            <div
              key={`${layer.kind}-${i}`}
              data-testid={`photo-box-${layer.kind}`}
              data-active={active ? "true" : "false"}
              style={{
                position: "absolute", ...r, boxSizing: "border-box",
                border: `3px ${active ? "solid" : "dashed"} ${BOX_STROKE}`,
                boxShadow: active ? "0 0 0 1px rgba(0,0,0,.35), inset 0 0 0 1px rgba(0,0,0,.25)" : "none",
                opacity: active ? 1 : 0.55, pointerEvents: "none",
              }}
            >
              <span style={{ position: "absolute", left: -3, top: -18, background: BOX_STROKE, color: "#111827", fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: "3px 3px 0 0", whiteSpace: "nowrap" }}>
                {layer.label}
              </span>
              {active && !readOnly && (
                <button
                  type="button"
                  aria-label="Remove box"
                  data-testid="photo-box-remove"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); onRemove(i); }}
                  style={{
                    position: "absolute", right: -10, top: -10, width: 18, height: 18, borderRadius: 999, border: "1px solid #111827",
                    background: BOX_STROKE, color: "#111827", fontSize: 11, fontWeight: 700, lineHeight: "16px", padding: 0, cursor: "pointer", pointerEvents: "auto",
                  }}
                >
                  ×
                </button>
              )}
            </div>
          );
        });
      })}
      {draft && fit && (() => {
        const r = px({ x: Math.min(draft.x0, draft.x1), y: Math.min(draft.y0, draft.y1), w: Math.abs(draft.x1 - draft.x0), h: Math.abs(draft.y1 - draft.y0) });
        return r ? <div data-testid="photo-box-draft" style={{ position: "absolute", ...r, border: `2px dashed ${BOX_STROKE}`, background: `${BOX_STROKE}22`, pointerEvents: "none" }} /> : null;
      })()}
    </div>
  );
}

// ─── Photos card ─────────────────────────────────────────────────────────────

/**
 * §8.3 pick mode (trainee rail): the "where" questions put the Photos card
 * into pick mode — purple outline, a banner with the count, checkbox
 * thumbnails and a select button on the main photo. `selected` is the same
 * `where[]` array the evidence chips edit, so the two surfaces never drift.
 */
export interface PhotoPick {
  selected: string[];
  onToggle: (target: string) => void;
  /** Question wording for the banner ("Where do you see it?"). */
  label: string;
}

export interface PhotoBoxPanelProps {
  podPhotos: SimulatorSnapshotPhoto[];
  verifiedPast: SimulatorSnapshotVerifiedPast[];
  verifiedPastCount: number;
  layers: PhotoBoxLayer[];
  /** Layer the current question edits (question id); null → boxes are shown but nothing is drawable. */
  activeKind: string | null;
  activeLabel?: string;
  onChangeActive: (boxes: PhotoBox[]) => void;
  /** Trainee pick mode (§8.3). Absent in the key editor. */
  pick?: PhotoPick | null;
  /** Ask the card to switch tabs (q6 = Yes → Verified past, §8.3). Applied whenever the value changes. */
  requestedTab?: "pod" | "past" | null;
  /** Hide the drawing banner when there is nothing to say (trainee view before any reveal). */
  quietBanner?: boolean;
  /** Runner: boxes are shown (the active question's solid) but never drawable or removable. */
  readOnly?: boolean;
}

export function PhotoBoxPanel({ podPhotos, verifiedPast, verifiedPastCount, layers, activeKind, activeLabel, onChangeActive, pick, requestedTab, quietBanner, readOnly }: PhotoBoxPanelProps) {
  const [tab, setTab] = useState<"pod" | "past">("pod");
  useEffect(() => { if (requestedTab) setTab(requestedTab); }, [requestedTab]);
  const pickSet = new Set(pick?.selected ?? []);
  const [activePhoto, setActivePhoto] = useState(0);
  const [activePast, setActivePast] = useState(0);
  const safeActive = activePhoto < podPhotos.length ? activePhoto : 0;
  const photo = podPhotos[safeActive] ?? null;
  const photoNo = photo?.index ?? safeActive + 1;
  const target = `photo:${photoNo}`;
  const safePast = activePast < verifiedPast.length ? activePast : 0;
  const pastEntry = verifiedPast[safePast] ?? null;

  const boxCountFor = (index: number) => layers.reduce((n, l) => n + l.boxes.filter((b) => b.target === `photo:${index}`).length, 0);
  const activeLayer = activeKind ? layers.find((l) => l.kind === activeKind) : undefined;
  const activePhotoBoxes = activeLayer?.boxes ?? [];
  const totalBoxes = layers.reduce((n, l) => n + l.boxes.length, 0);

  // When the question changes, show the photo that already carries its
  // boxes (a reopened key would otherwise sit on Photo 1 with the street
  // boxes hidden on Photo 2). Only on question change — never while drawing.
  // Re-runs when the question changes or its photo targets change (the key
  // arrives after mount), but not while drawing on the current photo.
  const activeTargetsKey = activePhotoBoxes.map((b) => b.target).join(",");
  useEffect(() => {
    if (activePhotoBoxes.length === 0) return;
    const current = podPhotos[safeActive]?.index;
    if (activePhotoBoxes.some((b) => b.target === `photo:${current}`)) return;
    const i = podPhotos.findIndex((p) => `photo:${p.index}` === activePhotoBoxes[0].target);
    if (i >= 0) { setActivePhoto(i); setTab("pod"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKind, activeTargetsKey]);

  const onAdd = (box: PhotoBox) => activeLayer && onChangeActive([...activeLayer.boxes, box]);
  const onRemove = (i: number) => activeLayer && onChangeActive(activeLayer.boxes.filter((_, j) => j !== i));

  const showBanner = !quietBanner || !!activeKind || totalBoxes > 0;
  return (
    <div
      data-testid="simulator-photo-box-panel"
      data-pick-mode={pick ? "true" : "false"}
      style={{
        background: T.card, border: `1px solid ${pick ? T.purple : T.border}`, borderRadius: 8, padding: 12, fontFamily: Starling.fontBody,
        boxShadow: pick ? `0 0 0 2px ${T.purpleSub}, 0 1px 2px rgba(16,24,40,.06)` : "0 1px 2px rgba(16,24,40,.06)",
      }}
    >
      <div style={{ display: "flex", gap: 4, marginBottom: 10, borderBottom: `1px solid ${T.border}` }}>
        <button type="button" data-testid="agent-detail-photo-tab-pod" onClick={() => setTab("pod")} style={tabStyle(tab === "pod")}>
          POD <CountBadge n={podPhotos.length} active={tab === "pod"} />
        </button>
        <button type="button" data-testid="agent-detail-photo-tab-past" onClick={() => setTab("past")} style={tabStyle(tab === "past")}>
          Verified past <CountBadge n={verifiedPast.length} active={tab === "past"} />
        </button>
      </div>

      {tab === "pod" ? (
        <>
          {pick && (
            <div
              data-testid="photo-pick-banner"
              data-count={pickSet.size}
              style={{ marginBottom: 8, padding: "6px 10px", borderRadius: 6, fontSize: 12, display: "flex", alignItems: "center", gap: 8, background: T.purpleSub, color: T.purple, border: `1px solid ${T.purple}` }}
            >
              <span style={{ fontWeight: 700 }}>☑</span>
              <span><strong>{pick.label}</strong> Tick every photo that shows it.</span>
              <span style={{ marginLeft: "auto", whiteSpace: "nowrap", fontWeight: 700 }}>{Array.from(pickSet).filter((s) => s.startsWith("photo:")).length} selected</span>
            </div>
          )}
          {showBanner && <div
            data-testid="photo-box-banner"
            data-active-kind={activeKind ?? ""}
            style={{
              marginBottom: 8, padding: "6px 10px", borderRadius: 6, fontSize: 12, display: "flex", alignItems: "center", gap: 8,
              background: activeKind ? `${BOX_STROKE}1F` : T.sub,
              color: activeKind ? BOX_INK : T.txt2,
              border: `1px solid ${activeKind ? `${BOX_STROKE}99` : T.border}`,
            }}
          >
            <span style={{ fontWeight: 700 }}>▭</span>
            {activeKind && readOnly ? (
              <span><strong>{activeLabel ?? activeKind}:</strong> the green boxes show where to look.</span>
            ) : activeKind ? (
              <span><strong>Drawing for {activeLabel ?? activeKind}.</strong> Drag on the photo to box it. Click × on a box to remove it.</span>
            ) : readOnly ? (
              <span>Green boxes show where earlier answers were found.</span>
            ) : (
              <span>Boxes drawn in the key show here.</span>
            )}
            <span style={{ marginLeft: "auto", whiteSpace: "nowrap", fontWeight: 600 }}>
              {activeLayer ? `${activeLayer.boxes.length} for this question` : ""}{activeLayer ? " · " : ""}{totalBoxes} total
            </span>
          </div>}

          {pick && photo && (
            <button
              type="button"
              data-testid={`photo-pick-main-${photoNo}`}
              aria-pressed={pickSet.has(target)}
              onClick={() => pick.onToggle(target)}
              style={{
                width: "100%", marginBottom: 8, padding: "7px 12px", borderRadius: 8, fontSize: 12.5, fontWeight: 700, fontFamily: Starling.fontBody, cursor: "pointer",
                border: `1px solid ${T.purple}`, background: pickSet.has(target) ? T.purple : T.card, color: pickSet.has(target) ? "#fff" : T.purple,
              }}
            >
              {pickSet.has(target) ? `✓ Photo ${photoNo} selected — click to unselect` : `Select Photo ${photoNo} as evidence`}
            </button>
          )}

          <PhotoDrawSurface
            key={photo?.url ?? "none"}
            url={photo?.url ?? null}
            target={target}
            caption={`Photo ${safeActive + 1} of ${podPhotos.length} · proof of delivery`}
            layers={layers}
            activeKind={activeKind}
            onAdd={onAdd}
            onRemove={onRemove}
            readOnly={readOnly}
          />

          <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
            {podPhotos.map((p, i) => {
              const n = boxCountFor(p.index);
              const picked = pickSet.has(`photo:${p.index}`);
              return (
                <div key={p.photoId ?? i} style={{ position: "relative" }}>
                  <button
                    type="button"
                    data-testid="agent-detail-pod-thumb"
                    onClick={() => setActivePhoto(i)}
                    title={`Photo ${p.index}${n ? ` · ${n} box${n === 1 ? "" : "es"}` : ""}`}
                    style={{
                      position: "relative", width: 54, height: 44, borderRadius: 5, padding: 0, cursor: "pointer", display: "block",
                      background: p.url ? `#000 url(${p.url}) center/cover no-repeat` : "linear-gradient(135deg, #6B7280, #374151)",
                      opacity: i === safeActive ? 1 : 0.65,
                      border: i === safeActive ? `2px solid ${T.purple}` : picked ? `2px solid ${T.purple}` : "2px solid transparent",
                    }}
                  >
                    {n > 0 && (
                      <span style={{ position: "absolute", top: 2, right: 2, background: "rgba(17,24,39,.85)", color: "#fff", fontSize: 9.5, fontWeight: 700, borderRadius: 999, padding: "0 5px", lineHeight: "14px" }}>
                        ▭{n}
                      </span>
                    )}
                  </button>
                  {pick && (
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={picked}
                      aria-label={`Photo ${p.index} shows it`}
                      data-testid={`photo-pick-thumb-${p.index}`}
                      onClick={(e) => { e.stopPropagation(); pick.onToggle(`photo:${p.index}`); }}
                      style={{
                        position: "absolute", left: 2, top: 2, width: 18, height: 18, borderRadius: 4, padding: 0, cursor: "pointer", lineHeight: "16px", fontSize: 12, fontWeight: 700,
                        border: `1px solid ${T.purple}`, background: picked ? T.purple : "rgba(255,255,255,.92)", color: picked ? "#fff" : T.purple,
                      }}
                    >
                      {picked ? "✓" : ""}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {verifiedPastCount > 0 && (
            <div data-testid="agent-detail-verified-banner" style={{ marginTop: 10, padding: "8px 10px", background: T.goodCalmBg, color: T.goodCalm, border: "1px solid rgba(22,101,52,.18)", borderRadius: 6, fontSize: 12, display: "flex", alignItems: "center", gap: 7 }}>
              <span>✓</span>
              <span><strong>{verifiedPastCount} verified past deliveries</strong> to this recipient.</span>
              <span style={{ color: T.text, opacity: 0.7, marginLeft: "auto", fontSize: 11, fontWeight: 500 }}>Tap Verified past to compare</span>
            </div>
          )}
        </>
      ) : (
        <div data-testid="agent-detail-verified-past">
          {verifiedPast.length === 0 ? (
            <div style={{ fontSize: 12, color: T.txt2 }}>No verified past deliveries yet.</div>
          ) : (
            <>
              <div
                data-testid="agent-detail-verified-past-main"
                style={{ aspectRatio: "4/3", borderRadius: 6, position: "relative", background: pastEntry?.photoUrl ? `#000 url(${pastEntry.photoUrl}) center/contain no-repeat` : "linear-gradient(160deg, #4B5563, #1F2937)" }}
              >
                <div style={captionStyle}>{pastCaption(pastEntry)}</div>
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                {verifiedPast.map((p, i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid="agent-detail-verified-past-thumb"
                    onClick={() => setActivePast(i)}
                    style={{
                      width: 54, height: 44, borderRadius: 5, padding: 0, cursor: "pointer",
                      background: p.photoUrl ? `#000 url(${p.photoUrl}) center/cover no-repeat` : "linear-gradient(135deg, #6B7280, #374151)",
                      opacity: i === safePast ? 1 : 0.65,
                      border: i === safePast ? `2px solid ${T.purple}` : "2px solid transparent",
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PhotoDrawSurface({ url, target, caption, layers, activeKind, onAdd, onRemove, readOnly }: {
  url: string | null;
  target: string;
  caption: string;
  layers: PhotoBoxLayer[];
  activeKind: string | null;
  onAdd: (box: PhotoBox) => void;
  onRemove: (index: number) => void;
  readOnly?: boolean;
}) {
  const { ref, size } = useElementSize<HTMLDivElement>();
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  useEffect(() => {
    // A cached image can be complete before React attaches onLoad; read its
    // natural size directly so saved boxes render on a reopened key.
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth) setNatural({ w: img.naturalWidth, h: img.naturalHeight });
  }, [url]);
  const fit = fitRect(size.w, size.h, natural.w, natural.h);
  return (
    <div
      ref={ref}
      data-testid="agent-detail-pod-main"
      data-drawable={activeKind && fit && !readOnly ? "true" : "false"}
      style={{ aspectRatio: "4/3", borderRadius: 6, position: "relative", overflow: "hidden", background: url ? "#000" : "linear-gradient(160deg, #4B5563, #1F2937)" }}
    >
      {url && (
        <img
          ref={imgRef}
          src={url}
          alt=""
          draggable={false}
          onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none" }}
        />
      )}
      <BoxOverlay target={target} fit={fit} layers={layers} activeKind={activeKind} onAdd={onAdd} onRemove={onRemove} readOnly={readOnly} />
      <div style={captionStyle}>{caption}</div>
    </div>
  );
}

const captionStyle: React.CSSProperties = {
  position: "absolute", bottom: 12, left: 12, background: "rgba(0,0,0,.45)", color: "#fff", fontSize: 11, padding: "3px 8px", borderRadius: 5, pointerEvents: "none", zIndex: 3,
};

function pastCaption(entry: SimulatorSnapshotVerifiedPast | null): string {
  if (!entry) return "—";
  const date = entry.completedAt ? new Date(entry.completedAt).toLocaleDateString() : "—";
  return `${date} · ${entry.dropZone ?? "—"} · verified by ${entry.verifiedBy ?? "agent"}`;
}

function CountBadge({ n, active }: { n: number; active: boolean }) {
  return (
    <span style={{ background: active ? T.purpleSub : T.sub, color: active ? T.purple : T.txt2, padding: "1px 6px", borderRadius: 999, fontSize: 10, fontWeight: 700, marginLeft: 6 }}>
      {n}
    </span>
  );
}

function tabStyle(active: boolean): React.CSSProperties {
  return {
    padding: "6px 12px", fontFamily: Starling.fontHead, fontSize: 12, fontWeight: 600,
    color: active ? T.purple : T.txt2, cursor: "pointer", background: "transparent", border: "none",
    borderBottom: `2px solid ${active ? T.purple : "transparent"}`, marginBottom: -1,
    display: "inline-flex", alignItems: "center",
    borderBottomLeftRadius: 0, borderBottomRightRadius: 0,
  };
}
